import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ImageUpload } from "@/components/ui/image-upload";
import { Search, AlertCircle, Music, Check, Filter } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Song } from '@/types/song';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDurationWithHours } from "@/lib/utils";
import axios from '@/lib/axios';

interface CreatePlaylistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: {
    id?: string;
    _id?: string;
    name: string;
    description?: string;
    artwork?: string;
    songs?: string[];
    genre?: string;
  };
  mode?: 'create' | 'edit';
}

// Genre değerini string olarak almak için yardımcı fonksiyon
const getGenreValue = (genre: string | {name: string, _id: string} | undefined): string => {
  if (!genre) return '';
  if (typeof genre === 'string') return genre;
  return genre.name;
};

export function CreatePlaylistModal({
  isOpen,
  onClose,
  onSuccess,
  initialData,
  mode = 'create'
}: CreatePlaylistModalProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [artwork, setArtwork] = useState<string | File>(initialData?.artwork || '');
  const [genre, setGenre] = useState(initialData?.genre || '');
  const [activeTab, setActiveTab] = useState<string>("details");

  // Şarkı ekleme için state'ler
  const [songs, setSongs] = useState<Song[]>([]);
  const [songsLoading, setSongsLoading] = useState<boolean>(false);
  const [songsError, setSongsError] = useState<string | null>(null);
  const [selectedSongs, setSelectedSongs] = useState<string[]>(initialData?.songs || []);
  const [searchValue, setSearchValue] = useState<string>('');
  const [genres, setGenres] = useState<string[]>([]);
  const [albums, setAlbums] = useState<string[]>([]);
  const [selectedGenreFilter, setSelectedGenreFilter] = useState<string>('all');
  const [selectedAlbum, setSelectedAlbum] = useState<string>('all');
  const [showFilters, setShowFilters] = useState<boolean>(true);

  // initialData değiştiğinde form alanlarını güncelle
  useEffect(() => {
    if (initialData) {
      setName(initialData.name || '');
      setDescription(initialData.description || '');
      setArtwork(initialData.artwork || '');
      setGenre(initialData.genre || '');
      setSelectedSongs(initialData.songs || []);
    }
  }, [initialData]);

  // Şarkıları yükle
  useEffect(() => {
    if (isOpen && activeTab === "songs") {
      loadSongs();
    }
  }, [isOpen, activeTab]);

  const loadSongs = async () => {
    setSongsLoading(true);
    setSongsError(null);
    
    try {
      const response = await axios.get('/songs?limit=5000');
      const songsData = response.data as Song[];
      
      // Şarkıları alfabetik olarak sırala
      songsData.sort((a: Song, b: Song) => {
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setSongs(songsData);
      
      // Tüm türleri ve albümleri çıkar
      const uniqueGenres: string[] = [];
      const uniqueAlbums: string[] = [];
      
      songsData.forEach(song => {
        // Genre işleme
        if (song.genre) {
          const genreValue = getGenreValue(song.genre);
          if (genreValue && !uniqueGenres.includes(genreValue)) {
            uniqueGenres.push(genreValue);
          }
        }
        
        // Album işleme
        if (song.album && typeof song.album === 'string' && !uniqueAlbums.includes(song.album)) {
          uniqueAlbums.push(song.album);
        }
      });
      
      setGenres(uniqueGenres);
      setAlbums(uniqueAlbums);
    } catch (error) {
      console.error('Şarkılar yüklenirken hata:', error);
      setSongsError('Şarkılar yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSongsLoading(false);
    }
  };

  // Şarkı seçme/kaldırma işlevi
  const toggleSongSelection = (songId: string) => {
    if (selectedSongs.includes(songId)) {
      setSelectedSongs(selectedSongs.filter(id => id !== songId));
    } else {
      setSelectedSongs([...selectedSongs, songId]);
    }
  };

  // Filtrelenmiş şarkıları getir
  const getFilteredSongs = () => {
    return songs.filter((song) => {
      // Arama filtresi
      const matchesSearch = 
        searchValue === '' || 
        (song.name && song.name.toLowerCase().includes(searchValue.toLowerCase())) ||
        (song.artist && song.artist.toLowerCase().includes(searchValue.toLowerCase())) ||
        (song.album && typeof song.album === 'string' && song.album.toLowerCase().includes(searchValue.toLowerCase()));
      
      // Tür filtresi
      const matchesGenre = 
        selectedGenreFilter === 'all' || 
        (song.genre && getGenreValue(song.genre) === selectedGenreFilter);
      
      // Albüm filtresi
      const matchesAlbum = 
        selectedAlbum === 'all' || 
        (song.album && typeof song.album === 'string' && song.album === selectedAlbum);
      
      return matchesSearch && matchesGenre && matchesAlbum;
    });
  };

  // Otomatik tür belirleme
  useEffect(() => {
    if (selectedSongs.length > 0 && songs.length > 0) {
      // Seçili şarkıların türlerini bul
      const selectedSongsData = songs.filter(song => selectedSongs.includes(song._id));
      const genreCounts: Record<string, number> = {};
      
      // Tür sayılarını hesapla
      selectedSongsData.forEach(song => {
        if (song.genre) {
          const genreValue = getGenreValue(song.genre);
          if (genreValue) {
            genreCounts[genreValue] = (genreCounts[genreValue] || 0) + 1;
          }
        }
      });
      
      // En çok kullanılan türü bul
      let mostCommonGenre = '';
      let maxCount = 0;
      
      Object.entries(genreCounts).forEach(([genre, count]) => {
        if (count > maxCount) {
          mostCommonGenre = genre;
          maxCount = count;
        }
      });
      
      // Eğer kullanıcı manuel olarak tür seçmediyse, otomatik belirle
      if (!genre && mostCommonGenre) {
        setGenre(mostCommonGenre);
      }
    }
  }, [selectedSongs, songs, genre]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      console.log('Form gönderiliyor:', { mode, name, description, artwork, genre, songs: selectedSongs });
      
      // Backend PATCH metodu kullanıyor (playlistRoutes.js'de "router.patch('/:id')" olarak tanımlanmış)
      const method = mode === 'create' ? 'POST' : 'PATCH';
      
      // Endpoint id'sini düzelt
      const endpoint = mode === 'create' 
        ? `${import.meta.env.VITE_API_URL}/playlists`
        : `${import.meta.env.VITE_API_URL}/playlists/${initialData?._id}`;
      
      // Eğer artwork bir File nesnesi ise (yeni yüklenen), FormData kullanmalıyız
      if (artwork instanceof File) {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('description', description);
        formData.append('artwork', artwork);
        formData.append('genre', genre);
        
        // Şarkıları ekle
        selectedSongs.forEach(songId => {
          formData.append('songs[]', songId);
        });
        
        const response = await fetch(endpoint, {
          method,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });
        
        if (!response.ok) {
          throw new Error('İşlem başarısız oldu');
        }
      } else {
        // Artwork bir URL string ise, JSON olarak gönderilir
        const payload = {
          name,
          description,
          artwork,
          genre,
          songs: selectedSongs
        };
        
        const response = await fetch(endpoint, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          throw new Error('İşlem başarısız oldu');
        }
      }

      toast({
        title: 'Başarılı',
        description: mode === 'create' 
          ? 'Playlist başarıyla oluşturuldu'
          : 'Playlist başarıyla güncellendi',
      });

      onSuccess();
      onClose();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: mode === 'create'
          ? 'Playlist oluşturulurken bir hata oluştu'
          : 'Playlist güncellenirken bir hata oluştu',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Yeni Playlist Oluştur' : 'Playlist Düzenle'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create' 
              ? 'Yeni bir playlist oluşturmak için gerekli bilgileri girin.'
              : 'Playlist bilgilerini güncelleyin.'
            }
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="details" className="flex-1 overflow-hidden flex flex-col" onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="details">Playlist Detayları</TabsTrigger>
            <TabsTrigger value="songs">Şarkı Ekle</TabsTrigger>
          </TabsList>
          
          <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col">
            <TabsContent value="details" className="flex-1 overflow-auto">
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Playlist Adı</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Playlist adını girin"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="description">Açıklama</Label>
                  <textarea
                    id="description"
                    className="min-h-[80px] w-full rounded-md border border-gray-300 bg-background px-3 py-2 text-sm resize-none"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Playlist açıklamasını girin"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="genre">Tür</Label>
                  <Select value={genre} onValueChange={setGenre}>
                    <SelectTrigger>
                      <SelectValue placeholder="Tür seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Seçilmedi</SelectItem>
                      {genres.map((genreOption) => (
                        <SelectItem key={genreOption} value={genreOption}>
                          {genreOption}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedSongs.length > 0 && !genre && (
                    <p className="text-xs text-muted-foreground">
                      Not: Şarkılar eklendiğinde tür otomatik olarak belirlenecektir.
                    </p>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label>Kapak Görseli</Label>
                  <ImageUpload
                    value={artwork}
                    onChange={setArtwork}
                    onRemove={() => setArtwork('')}
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="songs" className="flex-1 overflow-hidden flex flex-col">
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="search"
                      placeholder="Şarkı adı, sanatçı veya albüm ara..."
                      className="pl-8"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter className="h-4 w-4" />
                  </Button>
                </div>
                
                {showFilters && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label htmlFor="genre-filter">Tür Filtresi</Label>
                      <Select value={selectedGenreFilter} onValueChange={setSelectedGenreFilter}>
                        <SelectTrigger>
                          <SelectValue placeholder="Tür seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tüm Türler</SelectItem>
                          {genres.map((genre) => (
                            <SelectItem key={genre} value={genre}>
                              {genre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="album-filter">Albüm Filtresi</Label>
                      <Select value={selectedAlbum} onValueChange={setSelectedAlbum}>
                        <SelectTrigger>
                          <SelectValue placeholder="Albüm seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tüm Albümler</SelectItem>
                          {albums.map((album) => (
                            <SelectItem key={album} value={album}>
                              {album}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                
                {songsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <p className="text-muted-foreground">Şarkılar yükleniyor...</p>
                  </div>
                ) : songsError ? (
                  <Alert variant="destructive" className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{songsError}</AlertDescription>
                  </Alert>
                ) : (
                  <>
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-sm text-muted-foreground">
                        {getFilteredSongs().length} şarkı bulundu
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {selectedSongs.length} şarkı seçildi
                      </p>
                    </div>
                    
                    <ScrollArea className="flex-1 border rounded-md">
                      <div className="p-1">
                        {getFilteredSongs().length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-8">
                            <Music className="h-8 w-8 text-muted-foreground mb-2" />
                            <p className="text-muted-foreground">Şarkı bulunamadı</p>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            {getFilteredSongs().map((song) => (
                              <div
                                key={song._id}
                                className={`flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-accent ${
                                  selectedSongs.includes(song._id) ? 'bg-accent' : ''
                                }`}
                                onClick={() => toggleSongSelection(song._id)}
                              >
                                <div className={`w-5 h-5 rounded-md flex items-center justify-center ${
                                  selectedSongs.includes(song._id) 
                                    ? 'bg-primary text-primary-foreground' 
                                    : 'border border-input'
                                }`}>
                                  {selectedSongs.includes(song._id) && <Check className="h-3 w-3" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate">{song.name}</p>
                                  <div className="flex items-center text-xs text-muted-foreground">
                                    <span className="truncate">{song.artist}</span>
                                    {song.album && typeof song.album === 'string' && (
                                      <>
                                        <span className="mx-1">•</span>
                                        <span className="truncate">{song.album}</span>
                                      </>
                                    )}
                                    {song.genre && (
                                      <>
                                        <span className="mx-1">•</span>
                                        <span className="truncate">{getGenreValue(song.genre)}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className="text-xs text-muted-foreground whitespace-nowrap">
                                  {song.duration ? formatDurationWithHours(song.duration) : '--:--'}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </>
                )}
              </div>
            </TabsContent>
            
            <DialogFooter className="mt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                disabled={isLoading}
              >
                İptal
              </Button>
              <Button type="submit" disabled={isLoading || !name}>
                {isLoading ? 'Kaydediliyor...' : mode === 'create' ? 'Oluştur' : 'Güncelle'}
              </Button>
            </DialogFooter>
          </form>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
